import { useCallback, useEffect, useRef, useState } from 'react';
import type { Category, Product, Tenant } from '../../lib/database.types';
import { supabase } from '../../lib/supabase';
import { createCategory, updateCategory, deleteCategory, reorderCategories } from '../../lib/menuEditor';
import { syncCategoryEmojis } from '../../lib/menuImportService';
import { uniqueSlug } from '../../lib/menuManager';
import ConfirmDelete from './ConfirmDelete';

/**
 * Sous-onglet « Catégories » de la carte (parité legacy CategoriesTab).
 * Liste TOUTES les catégories (y compris masquées), réordonnable (↑↓), avec
 * emoji, nombre de produits, bascule de visibilité, édition et suppression
 * (impossible si la catégorie contient des produits ; protégée par mot de passe).
 */
export default function CategoriesManager({ tenant }: { tenant: Tenant }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ label: string; run: () => void } | null>(null);

  const reload = useCallback(async () => {
    const [{ data: cats }, { data: prods }] = await Promise.all([
      supabase.from('categories').select('*').eq('tenant_id', tenant.id).order('position'),
      supabase.from('products').select('id, category_id').eq('tenant_id', tenant.id),
    ]);
    const list = ((cats as unknown as Category[]) ?? []).sort((a, b) => a.position - b.position);
    const c: Record<string, number> = {};
    for (const p of (prods as unknown as Product[]) ?? []) c[p.category_id] = (c[p.category_id] ?? 0) + 1;
    setCategories(list);
    setCounts(c);
    setLoading(false);
  }, [tenant.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Auto-réparation des emojis manquants (catégories importées avant l'emoji auto).
  // Tourne une seule fois : remplit en base les emojis vides/génériques puis recharge.
  const healedRef = useRef(false);
  useEffect(() => {
    if (loading || healedRef.current) return;
    const needs = categories.some((c) => {
      const e = (c.emoji ?? '').trim();
      return e === '' || e === '🍴';
    });
    if (!needs) return;
    healedRef.current = true;
    syncCategoryEmojis(tenant.id).then(() => reload()).catch(() => {});
  }, [loading, categories, tenant.id, reload]);

  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await fn();
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const move = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= categories.length) return;
    const ids = categories.map((c) => c.id);
    [ids[index], ids[j]] = [ids[j], ids[index]];
    run(() => reorderCategories(ids))();
  };

  if (loading) return <p className="opacity-70">Chargement des catégories…</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold">Catégories du menu</h3>
          <p className="text-sm text-slate-500">
            {categories.length} catégorie{categories.length > 1 ? 's' : ''} · réorganisable, désactivable
          </p>
        </div>
        <button
          onClick={() => {
            setCreating(true);
            setEditing({ id: '', tenant_id: tenant.id, slug: '', name: '', emoji: '', position: categories.length, is_visible: true } as Category);
          }}
          className="rounded-lg px-3 py-2 text-sm font-semibold text-white"
          style={{ background: 'var(--brand-primary)' }}
        >
          + Nouvelle catégorie
        </button>
      </div>

      <ul className="flex flex-col gap-2">
        {categories.map((c, i) => (
          <li key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={busy || i === 0} className="text-xs disabled:opacity-30">▲</button>
              <button onClick={() => move(i, 1)} disabled={busy || i === categories.length - 1} className="text-xs disabled:opacity-30">▼</button>
            </div>
            <span className="text-2xl">{c.emoji || '🍴'}</span>
            <div className="min-w-0 flex-1">
              <div className="font-semibold">{c.name}</div>
              <div className="text-xs text-slate-500">
                {counts[c.id] ?? 0} produit{(counts[c.id] ?? 0) > 1 ? 's' : ''} · position {c.position}
              </div>
            </div>
            <button
              onClick={run(() => updateCategory(c.id, { is_visible: !c.is_visible }))}
              className={
                'rounded-md px-2 py-1 text-xs font-semibold ' +
                (c.is_visible ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500')
              }
            >
              {c.is_visible ? '✓ Active' : 'Désactivée'}
            </button>
            <button
              onClick={() => {
                setCreating(false);
                setEditing(c);
              }}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold hover:bg-slate-50"
            >
              Éditer
            </button>
            <button
              onClick={() => {
                if ((counts[c.id] ?? 0) > 0) {
                  alert('Impossible de supprimer une catégorie qui contient des produits. Déplacez ou supprimez d’abord ses produits.');
                  return;
                }
                setPendingDelete({ label: `la catégorie « ${c.name} »`, run: run(() => deleteCategory(c.id)) });
              }}
              className="text-slate-400 hover:text-red-600"
              title="Supprimer"
            >
              🗑️
            </button>
          </li>
        ))}
        {categories.length === 0 && <p className="text-sm opacity-60">Aucune catégorie. Cliquez « + Nouvelle catégorie ».</p>}
      </ul>

      {editing && (
        <CategoryModal
          category={editing}
          isNew={creating}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={async (fields) => {
            if (creating) {
              await run(() =>
                createCategory(tenant.id, {
                  name: fields.name,
                  slug: uniqueSlug(fields.name, categories.map((c) => c.slug)),
                  emoji: fields.emoji,
                  position: categories.length,
                }),
              )();
            } else {
              await run(() => updateCategory(editing.id, { name: fields.name, emoji: fields.emoji }))();
            }
            setEditing(null);
          }}
        />
      )}

      <ConfirmDelete
        open={!!pendingDelete}
        label={pendingDelete?.label ?? ''}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          pendingDelete?.run();
          setPendingDelete(null);
        }}
      />
    </div>
  );
}

function CategoryModal({
  category,
  isNew,
  busy,
  onCancel,
  onSave,
}: {
  category: Category;
  isNew: boolean;
  busy: boolean;
  onCancel: () => void;
  onSave: (fields: { name: string; emoji: string }) => void;
}) {
  const [name, setName] = useState(category.name);
  const [emoji, setEmoji] = useState(category.emoji ?? '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5">
        <h3 className="mb-3 text-lg font-bold">{isNew ? 'Nouvelle catégorie' : 'Modifier la catégorie'}</h3>
        <div className="mb-3 grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-semibold opacity-60">Nom</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 p-2" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">Emoji</label>
            <input value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4} className="w-full rounded-lg border border-slate-200 p-2 text-center" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold">Annuler</button>
          <button
            onClick={() => name.trim() && onSave({ name: name.trim(), emoji: emoji.trim() })}
            disabled={busy || !name.trim()}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            style={{ background: 'var(--brand-primary)' }}
          >
            {isNew ? 'Créer' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
